import { useCallback, useMemo, useState } from "react";

export const cellKey = (sku, size) => `${sku}||${size}`;

/**
 * Holds the cell quantities the user has typed, before they are sent anywhere.
 *
 * Only cells that actually differ from the parsed count are kept, so pressing
 * Save with nothing changed sends nothing, and typing a value back to what the
 * PDF said clears the edit instead of recording a no-op.
 */
export const useQuantityEdits = () => {
  const [edits, setEdits] = useState({});

  const reset = useCallback(() => {
    setEdits({});
  }, []);

  const setCell = useCallback((sku, size, rawValue, parsedValue) => {
    const key = cellKey(sku, size);
    setEdits((current) => {
      const next = { ...current };

      // An empty box means zero, not "unchanged".
      const value = rawValue === "" ? 0 : Number(rawValue);
      if (!Number.isInteger(value) || value < 0) return current;

      if (value === parsedValue) delete next[key];
      else next[key] = value;

      return next;
    });
  }, []);

  const resetCell = useCallback((sku, size) => {
    setEdits((current) => {
      const next = { ...current };
      delete next[cellKey(sku, size)];
      return next;
    });
  }, []);

  // What the API expects: one entry per changed cell.
  const overrides = useMemo(
    () =>
      Object.entries(edits).map(([key, quantity]) => {
        const [sku, size] = key.split("||");
        return { sku, size, quantity };
      }),
    [edits]
  );

  return {
    edits,
    overrides,
    hasChanges: overrides.length > 0,
    setCell,
    resetCell,
    reset,
  };
};

export default useQuantityEdits;
